import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Download, FileText, Loader2 } from 'lucide-react';
import { documentsApi, handleApiError } from '../lib/api';
import { useAuthStore } from '../store/auth';

interface Document {
  id: string;
  title: string;
  description?: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  createdAt: string;
  category?: { id: string; name: string };
}

export function DocumentViewer() {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuthStore();
  const [document, setDocument] = useState<Document | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDocument = async () => {
      if (!id) return;

      try {
        const response = await documentsApi.get(id);
        setDocument(response.data.data?.document);
      } catch (err) {
        setError(handleApiError(err));
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocument();
  }, [id]);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
        <p className="text-slate-400">{error || 'Documento no encontrado'}</p>
        <Link
          to="/documents"
          className="inline-flex items-center gap-2 mt-4 text-emerald-400 hover:text-emerald-300"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a documentos
        </Link>
      </div>
    );
  }

  const viewUrl = `${documentsApi.getViewUrl(document.id)}?token=${token}`;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/documents"
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-white">{document.title}</h1>
            <div className="flex items-center gap-3 text-sm text-slate-400 mt-1">
              {document.category && (
                <span className="px-2 py-0.5 bg-slate-800 rounded">
                  {document.category.name}
                </span>
              )}
              <span>{formatFileSize(document.fileSize)}</span>
              <span>{document.fileName}</span>
            </div>
          </div>
        </div>
        <a
          href={documentsApi.getDownloadUrl(document.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
        >
          <Download className="w-4 h-4" />
          Descargar
        </a>
      </div>

      {document.description && (
        <p className="text-slate-400">{document.description}</p>
      )}

      {/* PDF Viewer */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        <iframe
          src={viewUrl}
          className="w-full h-[calc(100vh-280px)] min-h-[500px]"
          title={document.title}
        />
      </div>
    </div>
  );
}
